import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) { setPermissions(null); setLoaded(false); return; }
    const cacheKey = `sprintai_permissions_${user.role}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        setPermissions(JSON.parse(cached));
        setLoaded(true);
      }
    } catch { /* ignore */ }
    api.get("/permissions").then(r => {
      setPermissions(r.data);
      setLoaded(true);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(r.data)); } catch { /* ignore */ }
    }).catch(() => setLoaded(true));
  }, [user]);

  const hasPerm = useCallback((page, action) => {
    if (!user) return false;
    // Admin always has full access as safety net
    if (user.role === "admin") return true;
    if (!permissions || !loaded) {
      // Fallback: use legacy role checks while permissions load
      return _legacyCheck(user.role, page, action);
    }
    const rolePerms = permissions[user.role];
    if (!rolePerms) return false;
    return rolePerms.pages?.[page]?.[action] === true;
  }, [user, permissions, loaded]);

  const hasAI = useCallback((feature) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!permissions || !loaded) {
      return _legacyAICheck(user.role, feature);
    }
    return permissions[user.role]?.ai?.[feature] === true;
  }, [user, permissions, loaded]);

  const hasFloating = useCallback((button) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!permissions || !loaded) {
      // Legacy fallback
      if (user.role === "client") return false;
      if (button === "slack") return true;
      return user.role === "manager" || user.role === "analyst";
    }
    return permissions[user.role]?.floating?.[button] === true;
  }, [user, permissions, loaded]);

  return (
    <PermissionContext.Provider value={{ hasPerm, hasAI, hasFloating, loaded }}>
      {children}
    </PermissionContext.Provider>
  );
}

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) return { hasPerm: () => false, hasAI: () => false, hasFloating: () => false, loaded: false };
  return ctx;
};

// Legacy fallbacks — same behavior as hardcoded checks before permissions system
function _legacyCheck(role, page, action) {
  const isManager = role === "manager" || role === "analyst";
  const isClient = role === "client";
  if (action === "view") {
    if (["dashboard", "projects", "bugs", "task_board"].includes(page)) return true;
    if (["developers", "sprints"].includes(page)) return !isClient;
    if (["standup", "daily_schedule", "on_board", "sprint_groups", "activity_logs"].includes(page)) return isManager;
    if (["admin_settings", "user_management", "chat_moderation", "ai_usage", "performance"].includes(page)) return false;
    if (page === "tasks") return !isClient;
    if (page === "sprint_reports") return true;
    if (page === "client_enquiries" || page === "change_requests") return !isClient || isClient;
    if (page === "slack") return !isClient;
  }
  if (isManager) return true;
  return false;
}

function _legacyAICheck(role, feature) {
  if (role === "manager" || role === "analyst") return true;
  if (feature === "chat") return role !== "client";
  return false;
}
